"use client";

import { COLOR, Dot, MeasureArrow, IllustrationCard } from "./_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── Risk illustrations ──────────────────────────────────────────────

const STRINGS: Record<Locale, Record<string, string>> = {
  ja: {
    volatilityTitle: "ボラティリティ",
    volatilityDescription: "日々の値動きの振れ幅",
    drawdownTitle: "最大下落",
    drawdownDescription: "いちばん深い下げ幅",
    recoveryTitle: "回復時間",
    recoveryDescription: "元に戻るまでの長さ",
    swing: "振れ幅",
    decline: "下落幅",
    timeToRecover: "回復までの時間",
  },
  en: {
    volatilityTitle: "Volatility",
    volatilityDescription: "How widely the price swings day to day",
    drawdownTitle: "Max drawdown",
    drawdownDescription: "The deepest fall from a peak",
    recoveryTitle: "Recovery time",
    recoveryDescription: "How long it takes to get back",
    swing: "Swing",
    decline: "Fall",
    timeToRecover: "Time to recover",
  },
};

const VW = 240;
const VH = 120;

function VolatilityIllustration() {
  const t = STRINGS[useDocLocale()];
  const c = COLOR.rose;
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full">
      <line x1={20} y1={58} x2={220} y2={58} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.1} />
      <path
        d="M 24 58 L 48 36 L 72 74 L 96 30 L 120 78 L 144 34 L 168 70 L 192 38 L 216 52"
        fill="none"
        stroke={c}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <MeasureArrow x1={228} y1={30} x2={228} y2={78} color={c} label={t.swing} />
    </svg>
  );
}

function MaxDrawdownIllustration() {
  const t = STRINGS[useDocLocale()];
  const c = COLOR.rose;
  const peakX = 78;
  const peakY = 24;
  const troughX = 170;
  const troughY = 92;
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full">
      <line x1={20} y1={peakY} x2={220} y2={peakY} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.08} />
      <path
        d="M 24 60 L 50 40 L 78 24 L 102 30 L 126 52 L 148 76 L 170 92 L 190 76 L 210 60 L 220 56"
        fill="none"
        stroke={c}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <line x1={peakX} y1={peakY} x2={peakX} y2={troughY} stroke={c} strokeWidth="0.8" strokeDasharray="3 3" />
      <line x1={troughX} y1={peakY} x2={troughX} y2={troughY} stroke={c} strokeWidth="0.8" strokeDasharray="3 3" />
      <Dot x={peakX} y={peakY} color={c} />
      <Dot x={troughX} y={troughY} color={c} />
      <MeasureArrow x1={228} y1={peakY} x2={228} y2={troughY} color={c} label={t.decline} />
    </svg>
  );
}

function RecoveryTimeIllustration() {
  const t = STRINGS[useDocLocale()];
  const c = COLOR.rose;
  const startX = 60;
  const endX = 200;
  const baseY = 30;
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full">
      <line x1={20} y1={baseY} x2={220} y2={baseY} stroke={c} strokeDasharray="3 5" strokeWidth="0.8" strokeOpacity={0.2} />
      <path
        d="M 24 44 L 44 34 L 60 30 L 76 38 L 96 66 L 114 84 L 136 72 L 160 50 L 184 36 L 200 30 L 220 24"
        fill="none"
        stroke={c}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <Dot x={startX} y={baseY} color={c} />
      <Dot x={endX} y={baseY} color={c} />
      <MeasureArrow x1={startX} y1={104} x2={endX} y2={104} color={c} label={t.timeToRecover} labelOffset={-4} />
    </svg>
  );
}

// ── Export ───────────────────────────────────────────────────────────

export function DocRiskTypesDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <IllustrationCard title={t.volatilityTitle} description={t.volatilityDescription}>
        <VolatilityIllustration />
      </IllustrationCard>
      <IllustrationCard title={t.drawdownTitle} description={t.drawdownDescription}>
        <MaxDrawdownIllustration />
      </IllustrationCard>
      <IllustrationCard title={t.recoveryTitle} description={t.recoveryDescription}>
        <RecoveryTimeIllustration />
      </IllustrationCard>
    </div>
  );
}
